import type { FC } from 'react';
import { alpha, Box, Stack, Typography, useTheme } from '@mui/material';
import type { Day as WeekDay, Locale } from 'date-fns';
import {
  format,
  getDate,
  isSameMonth,
  isToday,
  isWithinInterval,
} from 'date-fns';

import { Day } from './Day';
import { MonthHeader } from './MonthHeader';
import type { DateRange, Marker } from './types';
import { NavigationAction } from './types';
import {
  chunks,
  getDaysInMonth,
  inDateRange,
  isEndOfRange,
  isRangeSameDay,
  isStartOfRange,
} from './utils';

type MonthProps = {
  currentDate: Date;
  marker: Marker;
  dateRange: DateRange;
  minDate: Date;
  maxDate: Date;
  navState: [boolean, boolean];
  setMonth: (date: Date) => void;
  helpers: { isInHoverRange: (day: Date) => boolean };
  handlers: {
    handleClickDateNumber: (day: Date) => void;
    handleHoverDateNumber: (day: Date) => void;
    handleClickNavIcon: (marker: Marker, action: NavigationAction) => void;
  };
  locale?: Locale;
  hideOutsideMonthDays?: boolean;
};

export const Month: FC<MonthProps> = (props) => {
  const theme = useTheme();
  const {
    helpers,
    handlers,
    currentDate,
    dateRange,
    marker,
    setMonth,
    minDate,
    maxDate,
    locale,
    hideOutsideMonthDays,
  } = props;

  const weekStartsOn = locale?.options?.weekStartsOn ?? 0;
  const WEEK_DAYS = Array.from({ length: 7 }, (_, index) =>
    locale
      ? locale.localize?.day(((index + weekStartsOn) % 7) as WeekDay, {
          width: 'short',
          context: 'standalone',
        })
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index],
  );
  const [back, forward] = props.navState;

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          height: '55px',
          backgroundColor: alpha(theme.palette.grey[400], 0.1),
        }}
      >
        <MonthHeader
          minDate={minDate}
          maxDate={maxDate}
          currentDate={currentDate}
          setDate={setMonth}
          nextDisabled={!forward}
          prevDisabled={!back}
          onClickPrevious={() =>
            handlers.handleClickNavIcon(marker, NavigationAction.Previous)
          }
          onClickNext={() =>
            handlers.handleClickNavIcon(marker, NavigationAction.Next)
          }
          locale={locale}
        />
      </Stack>

      <Stack
        direction="row"
        justifyContent="center"
        sx={{ margin: '16px 24px 0 24px' }}
      >
        {WEEK_DAYS.map((day, index) => (
          <Box
            key={index}
            sx={{ width: '36px', display: 'flex', justifyContent: 'center' }}
          >
            <Typography color="textSecondary" sx={{ fontSize: '12px' }}>
              {day}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Stack direction="column" sx={{ margin: '24px' }}>
        {chunks(getDaysInMonth(currentDate, locale), 7).map((week, idx) => (
          <Stack key={idx} direction="row" justifyContent="center">
            {week.map((day) => {
              const isStart = isStartOfRange(dateRange, day);
              const isEnd = isEndOfRange(dateRange, day);
              const isRangeOneDay = isRangeSameDay(dateRange);
              const highlighted =
                inDateRange(dateRange, day) || helpers.isInHoverRange(day);

              return (
                <Day
                  key={format(day, 'dd-MM-yyyy')}
                  filled={isStart || isEnd}
                  outlined={isToday(day)}
                  highlighted={highlighted && !isRangeOneDay}
                  disabled={
                    !isSameMonth(currentDate, day) ||
                    !(
                      isWithinInterval(day, {
                        start: minDate,
                        end: maxDate,
                      }) ||
                      isStartOfRange(
                        { startDate: minDate, endDate: maxDate },
                        day,
                      )
                    )
                  }
                  hidden={!isSameMonth(currentDate, day)}
                  hideOutsideMonthDays={hideOutsideMonthDays}
                  startOfRange={isStart && !isRangeOneDay}
                  endOfRange={isEnd && !isRangeOneDay}
                  onClick={() => handlers.handleClickDateNumber(day)}
                  onHover={() => handlers.handleHoverDateNumber(day)}
                  value={getDate(day)}
                />
              );
            })}
          </Stack>
        ))}
      </Stack>
    </>
  );
};
